import { NavLink } from 'react-router-dom'
import {
  LayoutGrid, Briefcase, Package, Map, Truck, FileText,
  Factory, Users, Building2, Settings, Boxes, ChevronLeft, ChevronRight
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { get, set } from '@/lib/storage'

type Role = 'Admin'|'Dispatcher'|'Loader'|'Trucker'|'Accountant'
type Item = { to: string; label: string; icon: any; roles?: Role[] }

const ITEMS: Item[] = [
  { to: '/', label: 'Dashboard', icon: LayoutGrid },
  { to: '/jobs', label: 'Jobs', icon: Briefcase },
  { to: '/loads', label: 'Loads', icon: Truck },
  { to: '/loads/manifests', label: 'Manifests', icon: Boxes, roles:['Admin','Dispatcher','Accountant'] },
  { to: '/loads/inbox', label: 'Loads Inbox', icon: Truck, roles:['Admin','Dispatcher'] },
  { to: '/reports', label: 'Reports & Forms', icon: FileText },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/sites', label: 'Sites', icon: Map },
  { to: '/mills', label: 'Mills', icon: Factory },
  { to: '/contacts', label: 'Contacts', icon: Users },
  { to: '/customers', label: 'Customers', icon: Building2 },
  { to: '/admin/settings', label: 'Settings', icon: Settings, roles:['Admin'] },
]

export default function Sidebar(){
  const role = get<Role>('role','Admin')
  const [expanded, setExpanded] = useState(get<boolean>('ui:rail:expanded', false))
  useEffect(()=>{ set('ui:rail:expanded', expanded) }, [expanded])

  const items = ITEMS.filter(i => !i.roles || i.roles.includes(role))

  return (
    <aside
      className={[
        'relative h-screen sticky top-0 shrink-0',
        'bg-[#0b2019] text-white',
        'border-r border-[color:var(--border)]/40',
        'transition-[width] duration-200 ease-out',
        expanded ? 'w-60' : 'w-16',
        'z-30' // stay above header/main
      ].join(' ')}
      aria-label="Primary"
      aria-expanded={expanded}
    >
      {/* Floating edge toggle (prevents being covered when collapsed) */}
      <button
        className="
          absolute top-4 -right-3 z-40
          h-7 w-7 grid place-items-center rounded-full
          bg-white text-[#0b2019]
          shadow ring-1 ring-black/10 hover:bg-white/90
        "
        onClick={()=>setExpanded(v=>!v)}
        aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        title={expanded ? 'Collapse' : 'Expand'}
      >
        {expanded ? <ChevronLeft size={16}/> : <ChevronRight size={16}/>}
      </button>

      {/* Brand */}
      <div className="flex items-center gap-2 px-3 py-3">
        <div className="h-8 w-8 grid place-items-center rounded-lg bg-white/10">
          <span className="font-bold">C</span>
        </div>
        {expanded && <div className="text-sm font-semibold tracking-wide">CANOPY</div>}
      </div>

      {/* Nav */}
      <nav className="px-2 flex flex-col gap-1">
        {items.map(({to,label,icon:Icon})=>(
          <NavLink
            key={to}
            to={to}
            title={!expanded ? label : undefined}
            className={({isActive}) =>
              [
                'relative flex items-center gap-3 px-3 py-2 rounded-lg',
                'text-white/95 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30',
                isActive ? 'bg-white/12 ring-1 ring-white/15' : ''
              ].join(' ')
            }
          >
            <Icon size={18} className="shrink-0" />
            {expanded && <span className="truncate">{label}</span>}
          </NavLink>
        ))}
      </nav>
    </aside>
  )
}
